import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { type CallScript, CALL_SCRIPTS, getScriptByIndustry } from './callScripts';

interface CallScriptState {
  isOpen: boolean;
  script: CallScript | null;
  leadName: string;
}

interface CallScriptContextValue {
  state: CallScriptState;
  openScript: (scriptOrIndustry?: CallScript | string | null, leadName?: string) => void;
  closeScript: () => void;
  selectIndustry: (industryId: string) => void;
}

const CallScriptContext = createContext<CallScriptContextValue | null>(null);

export function CallScriptProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CallScriptState>({ isOpen: false, script: null, leadName: '' });

  const openScript = useCallback((scriptOrIndustry?: CallScript | string | null, leadName?: string) => {
    let resolvedScript: CallScript | null = null;
    if (scriptOrIndustry && typeof scriptOrIndustry === 'object' && 'sections' in scriptOrIndustry) {
      resolvedScript = scriptOrIndustry;
    } else if (typeof scriptOrIndustry === 'string' && scriptOrIndustry) {
      resolvedScript = getScriptByIndustry(scriptOrIndustry) || CALL_SCRIPTS[0];
    } else {
      resolvedScript = CALL_SCRIPTS[0];
    }
    setState({ isOpen: true, script: resolvedScript, leadName: leadName || 'Prospect' });
  }, []);

  const selectIndustry = useCallback((industryId: string) => {
    const nextScript = getScriptByIndustry(industryId);
    if (nextScript) {
      setState((prev) => ({ ...prev, script: nextScript }));
    }
  }, []);

  const closeScript = useCallback(() => {
    setState({ isOpen: false, script: null, leadName: '' });
  }, []);

  return (
    <CallScriptContext.Provider value={{ state, openScript, closeScript, selectIndustry }}>
      {children}
    </CallScriptContext.Provider>
  );
}

export function useCallScript() {
  const ctx = useContext(CallScriptContext);
  if (!ctx) throw new Error('useCallScript must be used within CallScriptProvider');
  return ctx;
}